import type { UserFileRead } from '@/uppy-file-upload/schemas'

/** Max visual nesting for email attachment trees (root email + two child levels). */
export const kEmailAttachmentMaxVisualDepth = 2

export type GroupedFile = {
  file: UserFileRead
  root: UserFileRead
  /** True ancestry depth in the listing (0 = root). */
  depth: number
  /** Indent/connector depth, clamped for dense display. */
  visualDepth: number
  parentId?: string
  parentName?: string
  /** Direct + nested extracted descendants present in this listing. */
  childCount: number
  hasChildren: boolean
}

const EMAIL_EXTENSIONS = new Set(['eml', 'msg'])
const EMAIL_CONTENT_TYPES = new Set(['message/rfc822', 'application/vnd.ms-outlook'])

/** True when the file is an email container by extension or content type. */
export function isEmailFile(file: Pick<UserFileRead, 'detected_extension' | 'detected_content_type' | 'original_filename'>): boolean {
  const extension = file.detected_extension?.replace(/^\./, '').toLowerCase()
  if (extension && EMAIL_EXTENSIONS.has(extension)) return true

  const contentType = file.detected_content_type?.split(';')[0]?.trim().toLowerCase()
  if (contentType && EMAIL_CONTENT_TYPES.has(contentType)) return true

  const filenameExtension = file.original_filename.split('.').pop()?.toLowerCase()
  return Boolean(filenameExtension && EMAIL_EXTENSIONS.has(filenameExtension))
}

function countDescendants(fileId: string, children: Map<string, UserFileRead[]>, visited = new Set<string>()): number {
  if (visited.has(fileId)) return 0
  visited.add(fileId)
  let total = 0
  for (const child of children.get(fileId) ?? []) {
    total += 1 + countDescendants(child.id, children, visited)
  }
  return total
}

/** Keep the requested server sort among roots and siblings while displaying attachment ancestry. */
export function groupExtractedFiles(files: UserFileRead[]): GroupedFile[] {
  const byId = new Map(files.map((file) => [file.id, file]))
  const children = new Map<string, UserFileRead[]>()
  const roots: UserFileRead[] = []

  for (const file of files) {
    const parentId = file.extracted_from_file_id
    const parent = parentId ? byId.get(parentId) : undefined
    if (!parent || parent.folder_id !== file.folder_id || parent.id === file.id) {
      roots.push(file)
      continue
    }
    const siblings = children.get(parent.id) ?? []
    siblings.push(file)
    children.set(parent.id, siblings)
  }

  const grouped: GroupedFile[] = []
  const seen = new Set<string>()
  const append = (file: UserFileRead, root: UserFileRead, depth: number, parentId?: string, parentName?: string) => {
    if (seen.has(file.id)) return
    seen.add(file.id)
    const childCount = countDescendants(file.id, children)
    grouped.push({
      file,
      root,
      depth,
      visualDepth: Math.min(depth, kEmailAttachmentMaxVisualDepth),
      parentId,
      parentName,
      childCount,
      hasChildren: childCount > 0,
    })
    for (const child of children.get(file.id) ?? []) {
      append(child, root, depth + 1, file.id, file.original_filename)
    }
  }
  for (const file of roots) append(file, file, 0)
  // Corrupt parent cycles still display every file once.
  for (const file of files) append(file, file, 0)
  return grouped
}

/**
 * Flat search rows: keep listing order and skip the expand tree, but reuse
 * groupExtractedFiles for childCount / parentId / parentName (folder match + cycle guards).
 */
export function flatSearchGroupedFiles(files: UserFileRead[]): GroupedFile[] {
  const groupedById = new Map(groupExtractedFiles(files).map((row) => [row.file.id, row]))
  return files.map((file) => {
    const grouped = groupedById.get(file.id)
    return {
      file,
      root: file,
      depth: 0,
      visualDepth: 0,
      parentId: grouped?.parentId,
      parentName: grouped?.parentName,
      childCount: grouped?.childCount ?? 0,
      hasChildren: false,
    }
  })
}

/**
 * Show a row when it is a root, or every ancestor in this listing is expanded.
 * Collapsed parents hide their descendants; multiple parents may stay open.
 */
export function filterVisibleGroupedFiles(grouped: GroupedFile[], expandedIds: ReadonlySet<string>): GroupedFile[] {
  const parentById = new Map(grouped.map((row) => [row.file.id, row.parentId]))

  return grouped.filter((row) => {
    let ancestorId = row.parentId
    while (ancestorId) {
      if (!expandedIds.has(ancestorId)) return false
      ancestorId = parentById.get(ancestorId)
    }
    return true
  })
}

/** Descendant count for a file id within an already-grouped listing. */
export function countExtractedDescendants(grouped: GroupedFile[], fileId: string): number {
  return grouped.find((row) => row.file.id === fileId)?.childCount ?? 0
}

/** Attachment count label for an email row; omitted when there are none. */
export function formatAttachmentCountLabel(childCount: number): string | undefined {
  if (childCount <= 0) return undefined
  return childCount === 1 ? '1 attachment' : `${childCount} attachments`
}
